// src/components/SignaturePreview.jsx
// Reusable email signature preview that matches the actual email signature sent by backend
// Always renders on white background to simulate email view

import React from 'react';
import '../styles/SignaturePreview.css';
import {
  isProfessionalSignatureHtml,
  sanitizeProfessionalSignatureHtml
} from '../utils/signatureBuilder';

/**
 * SignaturePreview - Displays email signature preview matching backend output
 *
 * @param {string} signatureMode - 'none', 'custom', or 'professional'
 * @param {string} emailSignature - Custom signature text (used when mode is 'custom')
 * @param {boolean} isLoading - Show loading state
 * @param {function} onSettingsClick - Optional callback for "Edit in Settings" button
 * @param {boolean} compact - Use compact styling for smaller containers
 */
export default function SignaturePreview({
  signatureMode,
  emailSignature,
  isLoading = false,
  onSettingsClick,
  compact = false
}) {
  const hasSavedSignature = Boolean(emailSignature && emailSignature.trim());

  // No signature configured
  if (signatureMode === 'none' || !signatureMode) {
    return (
      <div className="signature-preview-wrapper">
        <div className="signature-preview-empty">
          <span>No email signature configured</span>
          {onSettingsClick && (
            <button
              type="button"
              className="signature-preview-settings-btn"
              onClick={onSettingsClick}
            >
              Add in Settings
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="signature-preview-wrapper">
        <div className="signature-preview-loading">
          Loading signature...
        </div>
      </div>
    );
  }

  if (signatureMode === 'professional' && hasSavedSignature && isProfessionalSignatureHtml(emailSignature)) {
    const safeHtml = sanitizeProfessionalSignatureHtml(emailSignature);
    return (
      <div className="signature-preview-wrapper">
        <div
          className={`signature-preview-content signature-preview-professional-html ${compact ? 'signature-preview-content--compact' : ''}`}
          style={{ whiteSpace: 'normal' }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        {onSettingsClick && (
          <button
            type="button"
            className="signature-preview-settings-btn"
            onClick={onSettingsClick}
          >
            Edit in Settings
          </button>
        )}
      </div>
    );
  }

  // Custom signature text. Professional mode also uses saved non-HTML text for
  // backward compatibility, but never invents a company/person default.
  if (signatureMode === 'custom' || (signatureMode === 'professional' && hasSavedSignature)) {
    if (!hasSavedSignature) {
      return (
        <div className="signature-preview-wrapper">
          <div className="signature-preview-empty">
            <span>{signatureMode === 'professional' ? 'No professional signature text set' : 'No custom signature text set'}</span>
            {onSettingsClick && (
              <button
                type="button"
                className="signature-preview-settings-btn"
                onClick={onSettingsClick}
              >
                Edit in Settings
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="signature-preview-wrapper">
        <div className={`signature-preview-content ${compact ? 'signature-preview-content--compact' : ''}`}>
          <div className="signature-preview-custom">
            {emailSignature.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < emailSignature.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
          {onSettingsClick && (
            <button
              type="button"
              className="signature-preview-settings-btn"
              onClick={onSettingsClick}
            >
              Edit in Settings
            </button>
          )}
        </div>
      </div>
    );
  }

  // Professional mode requires user-created text. Empty settings stay empty
  // instead of rendering any company/person fallback.
  if (signatureMode === 'professional') {
    return (
      <div className="signature-preview-wrapper">
        <div className="signature-preview-empty">
          <span>No professional signature text set</span>
          {onSettingsClick && (
            <button
              type="button"
              className="signature-preview-settings-btn"
              onClick={onSettingsClick}
            >
              Edit in Settings
            </button>
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return null;
}
